import { Team } from '@litlingo/client';
import { saveCustomer } from 'actions';
import Permissions from 'components/Permissions';
import SelectRedesign from 'components/Select/SelectRedesign';
import useAllTeams from 'hooks/teams/useAllTeams';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getCustomer, getUser } from 'selectors/auth';
import { useSelector } from 'store';

type TeamS3BucketMapping = Record<string, string>;

type CustomerStorageConfig = {
  s3_bucket?: string | null;
  team_uuid_s3_buckets?: TeamS3BucketMapping;
};

const CustomerS3Buckets: React.FC = () => {
  const dispatch = useDispatch();
  const customer = useSelector(getCustomer);
  const user = useSelector(getUser);
  const { data: teamsData } = useAllTeams({ kind: 'division' });

  const storageConfig = customer.config as typeof customer.config & CustomerStorageConfig;
  const savedTeamMappings = storageConfig.team_uuid_s3_buckets ?? {};

  const [teamMappings, setTeamMappings] = useState<TeamS3BucketMapping>(savedTeamMappings);
  const savedBaselineRef = useRef(savedTeamMappings);
  const prevCustomerUuidRef = useRef(customer.uuid);
  const [selectedTeamUuid, setSelectedTeamUuid] = useState('');
  const [newBucketName, setNewBucketName] = useState('');

  useEffect(() => {
    if (prevCustomerUuidRef.current !== customer.uuid) {
      prevCustomerUuidRef.current = customer.uuid;
      savedBaselineRef.current = savedTeamMappings;
      setTeamMappings(savedTeamMappings);
      setSelectedTeamUuid('');
      setNewBucketName('');
      return;
    }

    const hasUnsavedChanges =
      JSON.stringify(teamMappings) !== JSON.stringify(savedBaselineRef.current);
    if (!hasUnsavedChanges) {
      setTeamMappings(savedTeamMappings);
      savedBaselineRef.current = savedTeamMappings;
    } else if (JSON.stringify(teamMappings) === JSON.stringify(savedTeamMappings)) {
      savedBaselineRef.current = savedTeamMappings;
    }
  }, [customer.uuid, JSON.stringify(savedTeamMappings)]); // eslint-disable-line react-hooks/exhaustive-deps

  const teamsByUuid = useMemo(() => {
    const map = new Map<string, Team>();
    (teamsData?.records ?? []).forEach((team) => {
      map.set(team.uuid, team);
    });
    return map;
  }, [teamsData?.records]);

  const availableTeams = useMemo(
    () =>
      (teamsData?.records ?? []).filter(
        (team) => !Object.prototype.hasOwnProperty.call(teamMappings, team.uuid)
      ),
    [teamMappings, teamsData?.records]
  );

  const teamOptions = availableTeams.map((team) => ({
    label: team.name,
    value: team.uuid,
  }));

  const canEdit = user.roles.includes('super-admin');

  const handleAddMapping = (): void => {
    if (!selectedTeamUuid || !newBucketName.trim()) {
      return;
    }

    setTeamMappings((current) => ({
      ...current,
      [selectedTeamUuid]: newBucketName.trim(),
    }));
    setSelectedTeamUuid('');
    setNewBucketName('');
  };

  const handleRemoveMapping = (teamUuid: string): void => {
    setTeamMappings((current) => {
      const next = { ...current };
      delete next[teamUuid];
      return next;
    });
  };

  const handleSave = (): void => {
    dispatch(
      saveCustomer({
        config: {
          ...customer.config,
          // @ts-ignore
          team_uuid_s3_buckets: teamMappings,
        },
      })
    );
  };

  const handleCancel = (): void => {
    setTeamMappings(savedBaselineRef.current);
    setSelectedTeamUuid('');
    setNewBucketName('');
  };

  const hasChanges = (): boolean =>
    JSON.stringify(teamMappings) !== JSON.stringify(savedBaselineRef.current);

  const defaultBucketLabel = storageConfig.s3_bucket || 'System default bucket';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-2 justify-between items-center">
        <div className="flex flex-row gap-2 items-base">
          <p className="text-menu-item">Storage</p>
          <p className="text-small">S3 bucket overrides by team</p>
        </div>
        <Permissions action="customers.advancedOptions">
          <div className="flex flex-row gap-2">
            <button
              type="button"
              className="button button--secondary flex justify-center w-20 h-8"
              onClick={handleCancel}
            >
              <span className="font-bold">Cancel</span>
            </button>
            <button
              data-testid="s3-buckets-save-button"
              type="button"
              className="button button--primary flex justify-center w-20 h-8"
              onClick={handleSave}
              disabled={!hasChanges()}
            >
              <span>Save</span>
            </button>
          </div>
        </Permissions>
      </div>

      <div className="flex flex-col bg-white border border-litlingo-gray-2 rounded">
        <div className="p-4 flex justify-between items-center border-b border-litlingo-gray-2">
          <div className="max-w-156 flex flex-col gap-1">
            <span className="text-body">Default bucket</span>
          </div>
          <span className="text-body font-mono">{defaultBucketLabel}</span>
        </div>

        <div className="p-4 flex flex-col gap-3">
          <span className="text-body">Team overrides</span>
          {Object.keys(teamMappings).length === 0 ? (
            <p className="text-small italic">No team overrides configured.</p>
          ) : (
            <div className="flex flex-col border border-litlingo-gray-2 rounded">
              <div className="grid grid-cols-12 gap-2 p-3 bg-litlingo-gray-0.5 text-small font-bold border-b border-litlingo-gray-2">
                <span className="col-span-5">Team</span>
                <span className="col-span-5">S3 bucket</span>
                <span className="col-span-2">Actions</span>
              </div>
              {Object.entries(teamMappings).map(([teamUuid, bucket]) => (
                <div
                  key={teamUuid}
                  className="grid grid-cols-12 gap-2 p-3 text-body border-b border-litlingo-gray-2 last:border-b-0 items-center"
                >
                  <span className="col-span-5">{teamsByUuid.get(teamUuid)?.name ?? teamUuid}</span>
                  <span className="col-span-5 font-mono">{bucket}</span>
                  <div className="col-span-2">
                    {canEdit && (
                      <button
                        type="button"
                        className="text-litlingo-alert text-small"
                        onClick={(): void => handleRemoveMapping(teamUuid)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {canEdit && (
            <div className="flex flex-row gap-3 items-end pt-2">
              <div className="flex flex-col gap-1 w-62">
                <span className="text-small">Team</span>
                <SelectRedesign
                  onChange={setSelectedTeamUuid}
                  options={teamOptions}
                  value={selectedTeamUuid}
                  placeholder="Select a team"
                  className="h-9"
                  dropdownClassname="h-60 overflow-auto custom-scrollbar"
                  disabled={teamOptions.length === 0}
                  dataTestid="s3-team-select"
                />
              </div>
              <label htmlFor="s3-bucket-input" className="flex flex-col gap-1 flex-1">
                <span className="text-small">S3 bucket</span>
                <input
                  id="s3-bucket-input"
                  data-testid="s3-bucket-input"
                  className="form-input h-9 py-2 px-4 border border-litlingo-gray-2 rounded-lg text-body"
                  value={newBucketName}
                  onChange={(e): void => setNewBucketName(e.target.value)}
                  placeholder="bucket-name.example.com"
                />
              </label>
              <button
                type="button"
                data-testid="s3-bucket-add-button"
                className="button button--secondary h-9 px-4"
                onClick={handleAddMapping}
                disabled={!selectedTeamUuid || !newBucketName.trim()}
              >
                Add
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerS3Buckets;
